/**
 * Normalizes the editor's existing Remix signals into a single lifecycle phase.
 *
 * Nothing here reads or mutates runtime state, and nothing here is authoritative: callers pass the
 * booleans the editor already computes for itself and get back the phase those booleans describe.
 * The result is reported to the product's lifecycle adapter for migration shadow comparison, so
 * this must stay a pure projection of existing signals rather than a second source of truth.
 *
 * Precedence mirrors the renderer's equivalent projection so both surfaces resolve an ambiguous
 * moment the same way: a terminal outcome wins over any in-flight phase, and a later phase wins
 * over an earlier one it supersedes.
 */
import type { EditorRemixLegacyPhase } from './types';

export type LegacyEditorRemixSignals = {
	/** The editor is waiting on generation — the thinking bar or decoration is live. */
	isThinking: boolean;
	/** Generation produced a previewable result. */
	isReady: boolean;
	/** The staging-area preview is on screen. */
	isPreviewVisible: boolean;
	/** Insertion into the document has begun. */
	isInserting: boolean;
	/** Generation failed, including safety and AUP violations. */
	isError: boolean;
	/** Generation was stopped from outside the editor, e.g. the Rovo side chat. */
	isAborted: boolean;
	/** The user dismissed or cancelled the flow from an editor surface. */
	isCancelled: boolean;
};

export const deriveLegacyEditorRemixPhase = ({
	isThinking,
	isReady,
	isPreviewVisible,
	isInserting,
	isError,
	isAborted,
	isCancelled,
}: LegacyEditorRemixSignals): EditorRemixLegacyPhase => {
	if (isError) {
		return 'error';
	}

	if (isAborted || isCancelled) {
		return 'aborted';
	}

	// Insertion outranks the preview because the preview stays mounted while content is written
	// into the document, so both signals are true for the whole of insertion.
	if (isInserting) {
		return 'inserting';
	}

	if (isPreviewVisible) {
		return 'previewing';
	}

	if (isReady) {
		return 'ready';
	}

	if (isThinking) {
		return 'thinking';
	}

	return 'inactive';
};
